import type { Distance } from "@pdfking/math"
import type { LayerID, Matrix, PlotShape } from "@pdfking/renderer-api"
import { PlotFunction } from "@pdfking/renderer-api"
import { BoxGrowList, DieCutStudioDieList } from "@pdfking/templates/box"
import { CoverGrowList } from "@pdfking/templates/cover"
import { FolderTemplateList } from "@pdfking/templates/folder"
import { RecordMap } from "@pdfking/tfunc"

import type { GrowPlotter, GrowSize, PlotContribute } from "#internal"
import { GrowList } from "#internal"

import { PlotBoxAuto, PlotBoxAutoWithFlaps } from "./boxes/PlotBoxAuto"
import { PlotBoxSimple } from "./boxes/PlotBoxSimple"
import { PlotPackaging, PlotPackagingAlt } from "./boxes/PlotPackaging"
import {
  PlotOval,
  PlotOvalHole,
  PlotRoundedCorners,
  PlotRoundedCornersHole,
  PlotTagHole,
} from "./boxes/PlotRoundedCorners"
import { PlotFolder } from "./folders/PlotFolder"
import { PlotGrowDie } from "./PlotGrowDie"
import { ResolveGrow } from "./ResolveGrow"

const BoxPlotters: Partial<Record<string, GrowPlotter>> = {
  bx1: PlotBoxSimple,
  bxa: PlotBoxAuto,
  bxaf: PlotBoxAutoWithFlaps,
  pack: PlotPackaging,
  packa: PlotPackagingAlt,
  bcv: PlotGrowDie(BoxGrowList.bcv),
  // Die Cut Studio's, drawn as theirs are
  ...RecordMap(PlotFolder, DieCutStudioDieList),
  cr: PlotGrowDie(CoverGrowList.cr),
  cs: PlotGrowDie(CoverGrowList.cs),
  r1: PlotRoundedCorners,
  r2: PlotOval,
  r1h: PlotRoundedCornersHole,
  r2h: PlotOvalHole,
  tgh: PlotTagHole,
  ...RecordMap(PlotFolder, FolderTemplateList),
}

/** The die-lines of a box grow or a folder — drawn by hand, as their
 * template draws them, as Die Cut Studio's boxes are, or read off the grow's
 * tree ({@link PlotGrowDie}) —, or
 * the outline a template grow is cut to, per layer, in the grow's own frame; nothing for any other grow. A plotter draws
 * the grow as it opens at the side: opening at the top, the grow lays its
 * side's pieces across the diagonal — the side's left edge on top, its top
 * edge on the left — so the die is drawn for the page turned and laid the
 * same way, its side-opening edge on top: turned about the grow's frame at
 * the side, which the die is drawn in whether its cut reaches every edge of
 * it or not. */
export const CreateExtraShapes = (
  grow_id: string,
  size: GrowSize | undefined,
  opening_top: boolean
): Partial<Record<LayerID, PlotContribute[LayerID]>> => {
  const plotter = BoxPlotters[grow_id]
  if (!plotter || !size) return {}
  if (!opening_top) return plotter(size)
  const at_side = { ...size, width: size.height, height: size.width }
  const side = plotter(at_side)
  // Each point (x, y) of the side's frame goes to (top - y, right - x): its
  // top edge on the left, its left edge on top, back where it was drawn.
  const frame = ResolveGrow(GrowList[grow_id as keyof typeof GrowList].layout, at_side, false).size
  const matrix: Matrix<Distance> = [0, -1, -1, 0, frame.height, frame.width]
  return RecordMap(
    shapes =>
      (shapes ?? []).length
        ? [{ enum: PlotFunction.PLOT_GROUP, matrix, shapes: [...(shapes ?? [])] } as PlotShape]
        : [],
    side
  )
}
